type GenerateImageUploadUrls = (count: number) => PromiseLike<{
  data?: {
    generateImageUploadUrls?: Array<{
      uploadURL?: string | null;
    } | null> | null;
  } | null;
}>;

type UploadImage = (
  path: string,
  localUrl: string,
  uploadUrl: string,
) => Promise<string | null>;

export type AvatarUploadRequest = {
  key: string;
  localUrl: string;
  path: string;
};

type AvatarUploadOptions = {
  generateImageUploadUrls: GenerateImageUploadUrls;
  requests: AvatarUploadRequest[];
  uploadImage: UploadImage;
};

async function uploadAvatarRequests({
  generateImageUploadUrls,
  requests,
  uploadImage,
}: AvatarUploadOptions): Promise<Record<string, string>> {
  if (requests.length === 0) {
    return {};
  }

  const { data } = await generateImageUploadUrls(requests.length);
  const uploadUrls = data?.generateImageUploadUrls;

  if (!uploadUrls || uploadUrls.length !== requests.length) {
    throw new Error('Could not generate image upload URLs.');
  }

  const uploadedAvatars = await Promise.all(
    requests.map(async (request, index) => {
      const uploadUrl = uploadUrls[index]?.uploadURL;

      if (!uploadUrl) {
        throw new Error('Missing image upload URL.');
      }

      const avatarId = await uploadImage(
        request.path,
        request.localUrl,
        uploadUrl,
      );

      if (!avatarId) {
        throw new Error('Image upload did not return an avatar id.');
      }

      return { avatarId, key: request.key };
    }),
  );
  const avatarIds: Record<string, string> = {};

  for (const uploadedAvatar of uploadedAvatars) {
    avatarIds[uploadedAvatar.key] = uploadedAvatar.avatarId;
  }

  return avatarIds;
}

export type AvatarUploadResult = { avatarId: string; ok: true } | { ok: false };

export type AvatarUploadsResult =
  | { avatarIds: Record<string, string>; ok: true }
  | { ok: false };

export async function uploadAvatars(
  options: AvatarUploadOptions,
): Promise<AvatarUploadsResult> {
  try {
    return { avatarIds: await uploadAvatarRequests(options), ok: true };
  } catch {
    return { ok: false };
  }
}

type SingleAvatarUploadOptions = Omit<AvatarUploadOptions, 'requests'> &
  Omit<AvatarUploadRequest, 'key'>;

export async function uploadAvatar({
  generateImageUploadUrls,
  localUrl,
  path,
  uploadImage,
}: SingleAvatarUploadOptions): Promise<AvatarUploadResult> {
  const key = path;
  const result = await uploadAvatars({
    generateImageUploadUrls,
    requests: [{ key, localUrl, path }],
    uploadImage,
  });

  if (!result.ok) {
    return result;
  }

  const avatarId = result.avatarIds[key];
  return avatarId ? { avatarId, ok: true } : { ok: false };
}
